import { Fragment, useMemo, useRef } from 'react';
import { createVerticalStrength, useDndScrolling } from 'react-dnd-scrolling';

import { DigitalQuote } from '@frontend/api';
import {
  OutlineCircleInfoIcon,
  TableContainer,
  TableV2,
  Tbody,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@frontend/components';
import { useDateFormat } from '@frontend/utils';

import { PAYMENT_METHOD, SHIPPING_TYPE } from 'constants/all';

import { useAppSelector } from 'store/hooks';
import { getOrder } from 'store/orders/selectors';

import { formatPrice } from 'utils/formatters';

import { DRAWER_MODES, SHIPPING_METHOD_LABEL } from '../constants';
import {
  getDrawerMode,
  getOrderedQuoteItemIds,
  getPaymentMethod,
  getQuoteItems,
  getSelectedOptions,
  getShippingCost,
  getShippingRate,
  getShippingType,
  getSubtotalCost,
  getTaxCost,
  getTotalCost,
} from '../data/reducer';
import { getShipsOnDateFromLeadTime } from '../utils';
import { BetweenOrderItemDropBorder } from './BetweenOrderItemDropBorder';
import { ReviewOrderRow } from './ReviewOrderRow';

// overrides the table theme's _notLast.td border so spacer rows stay invisible
const SPACER_ROW_SX = {
  '&:not(:last-child) > td': {
    borderBottomWidth: 0,
  },
} as const;

const verticalStrength = createVerticalStrength(24);

type ReviewOrderProps = {
  quote: DigitalQuote;
};

export const ReviewOrder = ({ quote }: ReviewOrderProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useDndScrolling(scrollContainerRef, { verticalStrength });

  const selectedOptions = useAppSelector(getSelectedOptions);
  const orderedQuoteItemIds = useAppSelector(getOrderedQuoteItemIds);
  const subtotal = useAppSelector(getSubtotalCost);
  const shippingCost = useAppSelector(getShippingCost);
  const shippingType = useAppSelector(getShippingType);
  const shippingRate = useAppSelector(getShippingRate);
  const paymentMethod = useAppSelector(getPaymentMethod);
  const quoteItems = useAppSelector(getQuoteItems);
  const quoteItemsById = useMemo(
    () =>
      Object.fromEntries(
        quoteItems.map((quoteItem) => [quoteItem.id, quoteItem]),
      ),
    [quoteItems],
  );
  const maxUnitPricePrecision = useMemo(() => {
    return Math.max(
      ...quoteItems.map(
        (quoteItem) => quoteItem.rootComponent.unitPricePrecision,
      ),
    );
  }, [quoteItems]);

  const drawerMode = useAppSelector(getDrawerMode);
  const order = useAppSelector(getOrder);
  const orderCreatedDate =
    drawerMode === DRAWER_MODES.EDIT && order ? order.created : undefined;

  const { toDateString: toPreferredDateFormat } = useDateFormat();

  const taxCost = useAppSelector((state) => getTaxCost(state, quote));
  const totalCost = useAppSelector((state) => getTotalCost(state, quote));
  const isChargeNowShipping =
    paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
    shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT &&
    Boolean(shippingRate?.token);

  const calculatedShippingMethodLabel = useMemo(() => {
    return shippingRate?.method
      ? SHIPPING_METHOD_LABEL[shippingRate.method]
      : '';
  }, [shippingRate?.method]);

  const footerColSpan = 7;

  return (
    <TableContainer
      ref={scrollContainerRef}
      border="none"
      borderRadius={0}
      size="sm"
      variant="simple"
      height="100%"
      overflow="auto"
    >
      <TableV2>
        <Thead position="sticky" top={0} zIndex={2}>
          <Tr>
            <Th width={3} />
            <Th width={3} />
            <Th maxW={23}>Part</Th>
            <Th textAlign="end">Qty</Th>
            <Th textAlign="end">Lead Time</Th>
            <Th>Ships On</Th>
            <Th textAlign="end">
              Unit Price
              <Tooltip label="Includes any discounts">
                <OutlineCircleInfoIcon
                  boxSize={2}
                  color="feature.secondary"
                  ml={0.25}
                />
              </Tooltip>
            </Th>
            <Th textAlign="end">
              Total Price
              <Tooltip label="Includes any discounts">
                <OutlineCircleInfoIcon
                  boxSize={2}
                  color="feature.secondary"
                  ml={0.25}
                />
              </Tooltip>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {orderedQuoteItemIds.map((quoteItemId, index) => {
            const selectedOption = selectedOptions[quoteItemId];
            if (!selectedOption) return null;

            const quoteItem = quoteItemsById[quoteItemId];
            if (!quoteItem) return null;

            const item = selectedOption.expedite ?? selectedOption.quantity;
            const leadTime =
              selectedOption.manualLeadTime ??
              item.manualLeadTime ??
              item.leadTime;
            const shipsOn =
              selectedOption.shipsOn ??
              getShipsOnDateFromLeadTime({
                fromDate: orderCreatedDate
                  ? new Date(orderCreatedDate)
                  : new Date(),
                leadTime,
                leadTimeDisplayUnit:
                  selectedOption.quantity.leadTimeDisplayUnit,
              }).toISOString();

            return (
              <Fragment key={quoteItemId}>
                <Tr sx={SPACER_ROW_SX} borderWidth={0} h={0} lineHeight={0}>
                  <Th
                    as="td"
                    colSpan={8}
                    position="relative"
                    borderWidth={0}
                    p={0}
                    h={0}
                    lineHeight={0}
                    bg="transparent"
                  >
                    <BetweenOrderItemDropBorder index={index} side="top" />
                  </Th>
                </Tr>
                <ReviewOrderRow
                  index={index}
                  quoteItemId={quoteItemId}
                  selectedOption={selectedOption}
                  quoteItem={quoteItem}
                  shipsOn={shipsOn}
                  toPreferredDateFormat={toPreferredDateFormat}
                />
              </Fragment>
            );
          })}
          {orderedQuoteItemIds.length > 0 && (
            <Tr sx={SPACER_ROW_SX} borderWidth={0} h={0} lineHeight={0}>
              <Th
                as="td"
                colSpan={8}
                position="relative"
                borderWidth={0}
                p={0}
                h={0}
                lineHeight={0}
                bg="transparent"
              >
                <BetweenOrderItemDropBorder
                  index={orderedQuoteItemIds.length}
                  side="bottom"
                />
              </Th>
            </Tr>
          )}
        </Tbody>
        <Tfoot>
          <Tr>
            <Th colSpan={footerColSpan} fontWeight="medium">
              Subtotal
            </Th>
            <Th as="td" textAlign="end" fontFamily="data" fontWeight="default">
              {formatPrice(subtotal, maxUnitPricePrecision)}
            </Th>
          </Tr>
          <Tr>
            <Th colSpan={footerColSpan} fontWeight="medium">
              Tax
            </Th>
            <Th as="td" textAlign="end" fontFamily="data" fontWeight="default">
              {formatPrice(taxCost, maxUnitPricePrecision)}
            </Th>
          </Tr>
          {isChargeNowShipping ? (
            <Tr>
              <Th colSpan={footerColSpan} fontWeight="medium">
                {calculatedShippingMethodLabel
                  ? `Calculated Shipping (${calculatedShippingMethodLabel})`
                  : 'Calculated Shipping'}
              </Th>
              <Th
                as="td"
                textAlign="end"
                fontFamily="data"
                fontWeight="default"
              >
                {formatPrice(shippingCost, maxUnitPricePrecision)}
              </Th>
            </Tr>
          ) : null}
          <Tr>
            <Th colSpan={footerColSpan}>Order Total</Th>
            <Th as="td" textAlign="end" fontFamily="data">
              {formatPrice(totalCost, maxUnitPricePrecision)}
            </Th>
          </Tr>
        </Tfoot>
      </TableV2>
    </TableContainer>
  );
};
